/* eslint-disable no-underscore-dangle */
import { Alert, Button, Col, Form, Input, Row, Typography } from 'antd';
import useIntl from 'hooks/useIntl';
import LocaleContext from 'locales';
import _ from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { kpassEvalCert, kpassEvalSuccess, kpassOtpGenerate, kpassOtpVerify } from '../service';

const styles = {
  formHeight: {
    height: 40,
  },
  spaceBetween: {
    display: 'flex',
    marginBlock: 4,
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  remaindTime: {
    display: 'inline-block',
    width: 32,
    textAlign: 'center',
  },
};

/**
 * Multi-Factor 인증
 * @param {object} credential {success(인증 성공 여부), twoFactor, ...}
 * @param {boolean} loginDispatch
 * @returns ReactNode
 */

function MultiFactor({ credential, loginDispatch }) {
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false); // 인증코드 생성 결과 {result: true, expireDate: "2022-05-02T08:56:06.784Z"}
  const [expired, setExpired] = useState(false); // 인증코드 만료 여부
  const { locale } = useContext(LocaleContext);
  const { formattedErrors } = useIntl();
  const [phoneForm] = Form.useForm();
  const [otpForm] = Form.useForm();

  useEffect(() => {
    if (expired) setError(locale.resource['txt.auth-expired']);
  }, [expired]);

  // FC: 인증코드 생성 요청 폼
  const InputPhoneForm = () => {
    const onFinish = async (values) => {
      setError(undefined); // sumit 할때마다 error를 clear 한다.
      setLoading(true);
      try {
        const res = await kpassOtpGenerate({ ...values, name: credential.name });
        if (res.success) {
          setGenerated({ result: res.success, expireDate: res.data.expireDate });
          loginDispatch({ type: 'set-credential', payload: { ...credential, ...values } });
        } else {
          const errors = formattedErrors(res.data);
          phoneForm.setFields(errors);
          setError(errors.find((el) => _.isEmpty(el.name)).errors);
        }
      } catch (e) {
        setError(e?.response?.data?.msg);
      }
      setLoading(false);
    };

    return (
      <Form style={{ width: '100%' }} onFinish={onFinish} form={phoneForm}>
        <Form.Item>{locale.resource['txt.kpass.input-telephoneNumber']}</Form.Item>
        <Form.Item
          label={locale.resource['txt.kpass.telephoneNumber']}
          name="telephoneNumber"
          style={{ flexWrap: 'nowrap' }}
          rules={[
            { required: true, message: locale.resource['txt.required-field'] },
            { max: 13 },
            {
              pattern: /^01[016789]-?\d{3,4}-?\d{4}$/,
              message: locale.resource['txt.kpass.validation-number-or-dash'],
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          style={{ ...styles.formHeight, marginTop: 16 }}
          loading={loading}
        >
          {locale.resource['txt.send-auth-code']}
        </Button>
      </Form>
    );
  };

  // FC: 인증코드 입력 폼
  const InputCodeForm = () => {
    const onFinish = async (values) => {
      setError(undefined); // sumit 할때마다 error를 clear 한다.
      setLoading(true);

      const res = await kpassOtpVerify({
        ..._.pick(credential, ['name', 'birth', 'email', 'telephoneNumber']),
        ...values,
      });
      if (res.success) {
        localStorage.setItem('token', res.data.token);
        loginDispatch({
          type: 'set-credential',
          payload: {
            ...credential,
            idpw: {
              ...credential.idpw,
              userId: res.data.userId, // userId로 localStorage.userId를 세팅하기 때문에 백엔드에서 만든 아이디를 리턴받아 세팅한다.(userId가 잘못되면 VD 목록이 출력되지 않음)
            },
            success: true,
          },
        });
        try {
          await kpassEvalCert({ infoAgreeYn: 'Y', pledgeYn: 'Y' });
          await kpassEvalSuccess(values.otpNumber);
        } catch (err) {
          console.error('KPASS 서버로 개인정보동의, 휴대폰인증완료 업데이트 중 오류 발생:', err);
        }
      } else {
        const errors = formattedErrors(res.data);
        otpForm.setFields(errors);
        setError(errors.find((el) => _.isEmpty(el.name)).errors);
      }
      setLoading(false);
    };

    // 인증코드 재요청
    const onCodeRegenerate = () => {
      setError(undefined);
      setGenerated(false);
      setExpired(false);
      setLoading(false);
    };

    return (
      <Form style={{ width: '100%' }} onFinish={onFinish} form={otpForm}>
        <div>{locale.resource['txt.enter-auth-code']}</div>
        <div style={styles.spaceBetween}>
          <Form.Item
            noStyle
            style={{ flex: 1 }}
            name="otpNumber"
            rules={[
              {
                required: true,
                message: locale.resource['txt.required-field'],
              },
            ]}
          >
            <Input
              size="large"
              style={{ flex: 1 }}
              placeholder={locale.resource['txt.enter-code']}
              disabled={expired}
              addonAfter={<RemainedTime generated={generated} setExpired={setExpired} />}
            />
          </Form.Item>
          <Button
            size="large"
            style={{ fontSize: 12 }}
            type={expired ? 'primary' : 'default'}
            onClick={onCodeRegenerate}
          >
            {locale.resource['txt.resend']}
          </Button>
        </div>
        <Button
          type="primary"
          block
          htmlType="submit"
          style={{ ...styles.formHeight, marginTop: 16 }}
          disabled={expired}
          loading={loading}
        >
          {locale.resource['txt.ok']}
        </Button>
      </Form>
    );
  };

  // 에러 메시지가 여러 개인 경우 대응, setError할 때 이미 locale이 적용되어 있다.
  const ErrorRender = ({ err }) => {
    return (
      <div style={{ whiteSpace: 'pre-line' }}>{typeof err === 'object' ? err.join('\n') : err}</div>
    );
  };

  return (
    <Row gutter={[0, 16]}>
      {error && (
        <Col span={24}>
          <Alert type="error" message={<ErrorRender err={error} />} />
        </Col>
      )}
      <Col span={24}>{generated.result ? <InputCodeForm /> : <InputPhoneForm />}</Col>
    </Row>
  );
}

/**
 * 발급된 인증코드의 남은 시간을 리턴하는 함수
 * 인증코드 유효 기간(3분) 동안 1초마다 렌더링 되기 때문에 별도 Component로 뺌
 * @param {boolean} generated 인증코드 발급 여부에 따라 타이머를 동작시킨다.
 * @param {boolean} setExpired 인증코드의 유효 시간이 만료되었는지 판별한다.
 * @returns ReactNode
 */
function RemainedTime({ generated, setExpired }) {
  const [result, setResult] = useState(undefined); // 만료시각과 현재시각 비교 값(예: 2:58)

  // 만료시각이 임박했는가?
  const impend = result?.split(':')[0] === '0' && Number(result.split(':')[1]) < 10 && true;

  // generated 되면 인증시간 타이머를 동작시킨다.
  useEffect(() => {
    const timer = setInterval(() => {
      if (generated.result) {
        const expireTime = moment(generated.expireDate); // 서버로 부터 받은 만료 시각
        const now = moment();
        const diff = moment.duration(expireTime.diff(now));

        if (diff._milliseconds > 0) {
          setResult(`${diff._data.minutes}:${diff._data.seconds}`);
        } else {
          setResult('0:0');
          setExpired(true);
          clearInterval(timer);
        }
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [generated]);

  return (
    <Typography.Text style={styles.remaindTime} type={impend ? 'danger' : null}>
      {result}
    </Typography.Text>
  );
}

export default MultiFactor;
